import React, { useState, useEffect, useCallback } from "react";
import { db } from "../../config/firebase";
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";

const ChampionshipBidVoting = () => {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votingStatus, setVotingStatus] = useState({});
  const [userRole, setUserRole] = useState(null);
  const { user } = useAuth();
  
  // Get user role on component mount
  useEffect(() => {
    const getUserRole = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setUserRole(userDoc.data().role);
          }
        } catch (error) {
          console.error("Error getting user role:", error);
        }
      }
    };
    
    getUserRole();
  }, [user]);

  const fetchBids = useCallback(async () => {
    try {
      setLoading(true);
      const bidsCollection = collection(db, "championshipBids");
      const snapshot = await getDocs(bidsCollection);
      const bidsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBids(bidsList);
      
      // Check which bids the current user has voted for
      const voteStatus = {};
      for (const bid of bidsList) {
        const hasVoted = bid.votes && bid.votes.includes(user.uid);
        voteStatus[bid.id] = hasVoted;
      }
      setVotingStatus(voteStatus);
    } catch (error) {
      console.error("Error fetching bids:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchBids();
  }, [fetchBids]);

  const handleVote = async (bidId, currentlyVoted) => {
    try {
      const bidRef = doc(db, "championshipBids", bidId);
      
      if (currentlyVoted) {
        // Remove vote
        await updateDoc(bidRef, {
          votes: arrayRemove(user.uid),
          voteCount: (bids.find(b => b.id === bidId)?.voteCount || 1) - 1
        });
      } else {
        // Add vote
        await updateDoc(bidRef, {
          votes: arrayUnion(user.uid),
          voteCount: (bids.find(b => b.id === bidId)?.voteCount || 0) + 1
        });
      }
      
      // Update local state
      setVotingStatus({
        ...votingStatus,
        [bidId]: !currentlyVoted
      });
      
      // Refresh bids to get updated vote counts
      fetchBids();
    } catch (error) {
      console.error("Error voting for bid:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Championship Bid Voting
          </h1>
          <div className="flex space-x-4">
            {userRole === 'admin' && (
              <Link
                to="/admin/bids"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Manage Bids
              </Link>
            )}
            <Link
              to="/admin"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Voting Instructions
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            As a gym administrator, you can vote for championship bids that you believe should be selected.
            Your vote helps determine which venues will host upcoming championships.
          </p>
          <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300 mb-4">
            <li>Click "View Details" to see complete information about each bid</li>
            <li>Use the "Vote" button to support a bid (you can change your vote at any time)</li>
            <li>The number of votes each bid has received is displayed</li>
          </ul>
        </div>

        {bids.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
            <p className="text-gray-600 dark:text-gray-300">
              No championship bid submissions found.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Club Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Venue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Proposed Dates
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Votes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {bids.map((bid) => (
                    <motion.tr
                      key={bid.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {bid.clubName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {bid.venueName || "Not specified"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {bid.proposedDates || "Not specified"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                          {bid.voteCount || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/admin/bids/${bid.id}`}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                        >
                          View Details
                        </Link>
                        <button
                          onClick={() => handleVote(bid.id, votingStatus[bid.id])}
                          className={`${
                            votingStatus[bid.id]
                              ? "text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                          }`}
                        >
                          {votingStatus[bid.id] ? "Voted ✓" : "Vote"}
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChampionshipBidVoting;
